import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Share2, ArrowLeft, CheckCircle, TrendingUp, Home, Zap } from 'lucide-react';
import FinalEstimateLoader from '../components/FinalEstimateLoader';
import { apiFetch } from '../utils/api';

const CostEstimate = () => {
  const [projectData, setProjectData] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [costEstimate, setCostEstimate] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const storedProjectData = localStorage.getItem('projectData');
    const storedAnswers = localStorage.getItem('finalAnswers');
    const storedCostEstimate = localStorage.getItem('finalCostEstimate');

    if (!storedProjectData || !storedAnswers) {
      navigate('/conceptual-estimate');
      return;
    }

    const projectData = JSON.parse(storedProjectData);
    const answers = JSON.parse(storedAnswers);
    const costEstimate = storedCostEstimate ? JSON.parse(storedCostEstimate) : null;

    setProjectData(projectData);
    setAnswers(answers);
    setCostEstimate(costEstimate);

    // Always get final cost breakdown and requirements
    console.log('📡 Loading cost breakdown data');
    getFinalEstimate(projectData, answers);
  }, [navigate]);

  const getFinalEstimate = async (projectData, answers) => {
    setLoading(true);
    
    // Small delay to ensure loader renders before API call
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const response = await apiFetch('final-estimate', {
        method: 'POST',
        body: JSON.stringify({
          projectData,
          answers
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ API Final Estimate:', result);
      
      setCostBreakdown(result.costBreakdown);
      setRequirements(result.requirements);
      
    } catch (error) {
      console.error('Error getting final estimate:', error);
      setError('Failed to load cost estimate: ' + (error.message || 'Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackBreakdown = () => ({
    divisions: {
      "General Requirements": 180000,
      "Site Construction": 150000,
      "Concrete": 320000,
      "Masonry": 120000,
      "Metals": 85000,
      "Wood & Plastics": 240000,
      "Thermal & Moisture Protection": 95000,
      "Openings": 160000,
      "Finishes": 280000,
      "Specialties": 45000,
      "Equipment": 35000,
      "Furnishings": 60000,
      "Special Construction": 25000,
      "Conveying Systems": 40000,
      "Mechanical": 220000,
      "Electrical": 180000
    }
  });

  // eslint-disable-next-line no-unused-vars -- kept for fallback if API returns no requirements
  const _generateFallbackRequirements = (answers) => [
    "Single family home construction",
    "Approximately 2,500 square feet",
    "Modern exterior design with mixed materials",
    "3 bedrooms, 2.5 bathrooms",
    "Open floor plan with luxury finishes",
    "Full basement with potential for finishing",
    "Energy efficient systems and appliances",
    "Attached 2-car garage",
    "Professional landscaping included"
  ];

  // --- Derived metrics for range, sq ft, $/sq ft, and additions ---
  const getSqFt = () => {
    // Prefer costEstimate.sq_ft if present
    if (costEstimate && typeof costEstimate.sq_ft === 'number') return costEstimate.sq_ft;
    // Try to parse from requirements text like "2,500 square feet" or "2500 sq ft"
    const text = (requirements || []).join(' ').toLowerCase();
    const match = text.match(/(\d{3,5})(?=\s*(sq\.?\s*ft|square\s*feet))/);
    if (match) {
      const num = parseInt(match[1].replace(/[,\s]/g, ''), 10);
      if (!Number.isNaN(num)) return num;
    }
    return null;
  };

  const getCostRangeDisplay = () => {
    const range = getUnifiedCostRange();
    return {
      min: Math.round(range.min),
      max: Math.round(range.max)
    };
  };

  const getCostPerSqFtRange = () => {
    const sqFt = getSqFt();
    if (!sqFt || sqFt <= 0) return null;
    const { min, max } = getCostRangeDisplay();
    return { min: Math.round(min / sqFt), max: Math.round(max / sqFt) };
  };

  const getAdditionsFromBaseModel = () => {
    const features = (costEstimate && Array.isArray(costEstimate.features)) ? costEstimate.features : [];
    const flattened = [];
    features.forEach(item => {
      if (item && typeof item === 'object') {
        const [name, val] = Object.entries(item)[0] || [];
        if (name) flattened.push({ name, delta: typeof val === 'number' ? val : 0 });
      }
    });
    return flattened;
  };

  const downloadEstimate = async () => {
    try {
      const response = await apiFetch('download-estimate', {
        method: 'POST',
        body: JSON.stringify({
          projectData,
          answers,
          costBreakdown,
          requirements
        })
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cost-estimate-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading estimate:', error);
    }
  };

  const shareEstimate = () => {
    const displayCost = getUnifiedTotalCost();
    if (navigator.share) {
      navigator.share({
        title: 'Construction Cost Estimate',
        text: `Construction cost estimate: $${(displayCost / 1000000).toFixed(2)} Million`,
        url: window.location.href
      }).catch((error) => {
        // Handle user cancellation silently
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      });
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link copied to clipboard!');
      }).catch((error) => {
        console.error('Error copying to clipboard:', error);
      });
    }
  };

  // Unified cost calculation - single source of truth
  const getUnifiedTotalCost = () => {
    // Always prioritize costEstimate.max when available (from final API call)
    // This ensures consistency with the final estimate that user completed
    if (costEstimate && costEstimate.max) {
      console.log('Using costEstimate.max:', costEstimate.max);
      return costEstimate.max;
    }
    // Fallback to breakdown sum if costEstimate not available
    if (costBreakdown && costBreakdown.divisions && Object.keys(costBreakdown.divisions).length > 0) {
      const sum = Object.values(costBreakdown.divisions).reduce((sum, cost) => sum + cost, 0);
      console.log('Using breakdown sum:', sum);
      return sum;
    }
    // Final fallback
    console.log('Using default fallback');
    return 2030000;
  };

  // Unified range calculation based on the same total cost
  const getUnifiedCostRange = () => {
    const totalCost = getUnifiedTotalCost();
    
    // Ensure totalCost is valid and greater than 0
    if (!totalCost || totalCost <= 0) {
      console.warn('Invalid totalCost, using fallback range');
      return {
        min: 0,
        max: 0
      };
    }
    
    // If we have costEstimate min/max and they're reasonable, use them
    if (costEstimate && costEstimate.min && costEstimate.max && 
        costEstimate.min > 0 && costEstimate.max > 0 &&
        Math.abs(costEstimate.max - totalCost) < totalCost * 0.5) {
      return {
        min: costEstimate.min,
        max: costEstimate.max
      };
    }
    
    // Otherwise, calculate ±10% range from unified total
    const min = totalCost * 0.9;
    const max = totalCost * 1.1;
    
    // Ensure we have valid values
    if (min <= 0 || max <= 0 || !isFinite(min) || !isFinite(max)) {
      console.warn('Invalid calculated range, using totalCost as both min and max');
      return {
        min: totalCost,
        max: totalCost
      };
    }
    
    return {
      min: min,
      max: max
    };
  };

  if (loading) {
    return <FinalEstimateLoader projectData={projectData} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Cost Estimate</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalCost = getUnifiedTotalCost();
  const costRange = getUnifiedCostRange();
  const sqFtValue = getSqFt();
  const cpsfRange = getCostPerSqFtRange();
  const additions = getAdditionsFromBaseModel();
  
  // Debug logging to identify inconsistency
  console.log('🔍 Cost Consistency Debug:');
  console.log('- costBreakdown exists:', !!costBreakdown);
  console.log('- costBreakdown.divisions exists:', !!(costBreakdown && costBreakdown.divisions));
  console.log('- costEstimate exists:', !!costEstimate);
  console.log('- costEstimate.max:', costEstimate?.max);
  console.log('- totalCost (unified):', totalCost);
  console.log('- totalCost formatted:', (totalCost / 1000000).toFixed(2) + 'M');
  console.log('- totalCost localized:', totalCost.toLocaleString());
  if (costBreakdown && costBreakdown.divisions) {
    const breakdownSum = Object.values(costBreakdown.divisions).reduce((sum, cost) => sum + cost, 0);
    console.log('- breakdown sum:', breakdownSum);
    console.log('- breakdown formatted:', (breakdownSum / 1000000).toFixed(2) + 'M');
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-white via-gray-50 to-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full shadow-lg">
              <CheckCircle className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Cost Estimate Complete</h1>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 text-xs sm:text-sm text-gray-600">
                <span className="font-medium">
                  {projectData?.constructionType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className="hidden sm:block w-1 h-1 bg-gray-400 rounded-full"></span>
                <span>{projectData?.city}, {projectData?.state}</span>
              </div>
            </div>
          </div>
          
          {/* Enhanced Summary in Header */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 lg:space-x-6 w-full sm:w-auto">
            <button
              onClick={() => navigate('/conceptual-estimate')}
              className="btn-secondary flex items-center justify-center space-x-2 text-sm px-4 py-3 shadow-md hover:shadow-lg transition-shadow"
            >
              <ArrowLeft size={16} />
              <span>New Estimate</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Prioritizing Key Information */}
      <div className="px-4 sm:px-6 py-4">
        {/* KPI Row: Range, Sq Ft, $/Sq Ft, Additions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-600 mb-1 font-medium">Cost Range</div>
            <div className="text-lg font-bold text-gray-900">
              {costRange.min > 0 && costRange.max > 0 ? (
                `${(costRange.min / 1000000).toFixed(1)}M - ${(costRange.max / 1000000).toFixed(1)}M`
              ) : (
                <span className="text-gray-400">—</span>
              )}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-600 mb-1 font-medium">Sq Ft</div>
            <div className="text-lg font-bold text-gray-900">{sqFtValue ? sqFtValue.toLocaleString() : '—'}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-600 mb-1 font-medium">Cost per Sq Ft</div>
            <div className="text-lg font-bold text-gray-900">
              {cpsfRange ? `$${cpsfRange.min.toLocaleString()} - $${cpsfRange.max.toLocaleString()}` : '—'}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-600 mb-1 font-medium">Additions From Base</div>
            <div className="text-lg font-bold text-gray-900">{additions.length || 0}</div>
          </div>
        </div>
        {/* Enhanced Quick Actions Bar */}
        <div className="bg-gradient-to-r from-white to-gray-50 rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Zap size={16} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">Estimate Generated</div>
                  <div className="text-xs text-gray-600">{new Date().toLocaleDateString()}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle size={16} className="text-green-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">Questions Answered</div>
                  <div className="text-xs text-gray-600">{answers.length} of 10 completed</div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={downloadEstimate}
                className="btn-primary flex items-center space-x-2 text-sm px-5 py-3 shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Download size={16} />
                <span>Download</span>
              </button>
              <button
                onClick={shareEstimate}
                className="btn-secondary flex items-center space-x-2 text-sm px-5 py-3 shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Share2 size={16} />
                <span>Share</span>
              </button>
              <button
                onClick={() => navigate('/drawing-estimate')}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium px-4 py-2 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors duration-200"
              >
                Upload Drawings →
              </button>
            </div>
          </div>
        </div>

        {/* Main KPIs - Cost Breakdown and Requirements (First View) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Cost Breakdown - 2/3 width */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100">
              {/* Header with Total */}
              <div className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50 rounded-t-lg p-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <TrendingUp size={24} className="text-primary-500" />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Cost Breakdown by Division</h2>
                      <p className="text-sm text-gray-600">Detailed cost analysis by construction category</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">
                      {totalCost > 0 ? `$${(totalCost / 1000000).toFixed(2)}M` : '—'}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">Total Project Cost</div>
                    {costRange.min > 0 && costRange.max > 0 && (
                      <div className="text-xs text-primary-600 mt-1">±10% accuracy range</div>
                    )}
                  </div>
                </div>
              </div>
              
                {/* Compact Table with Inline Indicators */}
              <div className="p-3">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-2 px-3 font-semibold text-gray-900 text-sm">Division</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-900 text-sm">Amount</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-900 text-sm">% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((costBreakdown && Object.entries(costBreakdown.divisions)) || 
                        Object.entries(generateFallbackBreakdown().divisions))
                        .sort(([,a], [,b]) => b - a)
                        .map(([division, cost], index) => {
                          const percentage = ((cost / totalCost) * 100).toFixed(1);
                          const isTopCategory = index < 3;
                          const divisionsToUse = costBreakdown ? costBreakdown.divisions : generateFallbackBreakdown().divisions;
                          const maxCost = Math.max(...Object.values(divisionsToUse));
                          const intensity = (cost / maxCost);
                          
                          // Create visual intensity indicator
                          const getIntensityColor = (intensity, isTop) => {
                            if (isTop) {
                              if (intensity > 0.8) return 'bg-red-500';
                              if (intensity > 0.6) return 'bg-orange-500';
                              return 'bg-yellow-500';
                            } else {
                              if (intensity > 0.4) return 'bg-blue-500';
                              if (intensity > 0.2) return 'bg-blue-400';
                              return 'bg-gray-400';
                            }
                          };
                          
                          const getIntensitySize = (intensity) => {
                            if (intensity > 0.8) return 'w-3 h-3';
                            if (intensity > 0.6) return 'w-2.5 h-2.5';
                            if (intensity > 0.4) return 'w-2 h-2';
                            return 'w-1.5 h-1.5';
                          };
                          
                          return (
                            <tr key={division} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                              <td className="py-2 px-3">
                                <div className="flex items-center space-x-2">
                                  <div className={`${getIntensityColor(intensity, isTopCategory)} ${getIntensitySize(intensity)} rounded-full flex-shrink-0`}></div>
                                  <span className={`font-medium text-sm ${isTopCategory ? 'text-gray-900' : 'text-gray-700'}`}>
                                    {division}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2 px-3 text-right">
                                <span className={`font-bold ${isTopCategory ? 'text-gray-900' : 'text-gray-700'}`}>
                                  ${(cost / 1000).toFixed(0)}K
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right">
                                <div className="flex items-center justify-end space-x-2">
                                  <span className={`font-medium text-sm ${isTopCategory ? 'text-gray-900' : 'text-gray-600'}`}>
                                    {percentage}%
                                  </span>
                                  {/* Mini bar indicator */}
                                  <div className="w-8 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${getIntensityColor(intensity, isTopCategory)}`}
                                      style={{ width: `${intensity * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                
                {/* Compact Legend */}
                <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span>Highest</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Lower</span>
                    </div>
                  </div>
                  <span className="font-medium">Total: ${(totalCost / 1000000).toFixed(2)}M</span>
                </div>
              </div>
            </div>

            {/* Additions From Base Model - Directly below Cost Breakdown */}
            <div className="mt-3 bg-white rounded-lg shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Additions from Base Model</h2>
                <p className="text-xs text-gray-600">Upgrades and selections contributing to cost</p>
              </div>
              <div className="p-4">
                {additions.length > 0 ? (
                  <div className="space-y-2">
                    {additions.map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 rounded border px-3 py-2">
                        <span className="text-gray-800 font-medium">{f.name}</span>
                        <span className="text-gray-900 font-semibold">{f.delta > 0 ? `+$${(f.delta/1000).toFixed(0)}K` : '+$0'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">No specific additions detected.</div>
                )}
              </div>
            </div>
          </div>

          {/* Project Requirements - 1/3 width */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 flex flex-col">
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Project Requirements</h2>
                  <div className="flex items-center space-x-2">
                    <Home size={18} className="text-primary-500" />
                    <span className="text-sm font-medium text-primary-600">{requirements.length} items</span>
                  </div>
                </div>
                
                {/* Requirements List - Takes available space */}
                <div className="space-y-2 flex-1">
                  {requirements
                    .filter(requirement => {
                      // Filter out cost range requirements to avoid inconsistency
                      const lowerReq = requirement.toLowerCase();
                      return !lowerReq.includes('total project cost range') && 
                             !lowerReq.includes('cost range') &&
                             !lowerReq.includes('estimated cost:') &&
                             !lowerReq.match(/\$\d+m\s*-\s*\$\d+m/i);
                    })
                    .map((requirement, index) => (
                    <div key={index} className="flex items-start space-x-3 p-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100 transition-all duration-200 hover:shadow-sm">
                      <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700 leading-relaxed font-medium">{requirement}</span>
                    </div>
                  ))}
                  
                  {/* Add consistent cost range from unified calculation */}
                  {costRange.min > 0 && costRange.max > 0 && (
                    <div className="flex items-start space-x-3 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 transition-all duration-200 hover:shadow-sm">
                      <CheckCircle size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700 leading-relaxed font-medium">
                        Total project cost range: ${(costRange.min / 1000000).toFixed(1)}M - ${(costRange.max / 1000000).toFixed(1)}M
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Enhanced Project Info - Fixed at bottom */}
                <div className="mt-6 pt-4 border-t border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Zap size={16} className="text-blue-500" />
                    <h4 className="font-semibold text-gray-900 text-sm">Project Overview</h4>
                  </div>
                  <div className="grid grid-cols-1 gap-3 text-xs">
                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                      <span className="text-gray-600 font-medium">Type:</span>
                      <span className="font-semibold text-gray-900">
                        {projectData?.constructionType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                      <span className="text-gray-600 font-medium">Location:</span>
                      <span className="font-semibold text-gray-900">{projectData?.city}, {projectData?.state}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                      <span className="text-gray-600 font-medium">Generated:</span>
                      <span className="font-semibold text-gray-900">{new Date().toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                      <span className="text-gray-600 font-medium">Total estimated cost:</span>
                      <span className="font-semibold text-gray-900">
                        {totalCost > 0 ? `$${totalCost.toLocaleString()}` : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CostEstimate;











